import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import { PasswordRequirements } from '../components/AuthCard';
import { api } from '../utils/api';

const SecuritySettings = ({ user, onLogout }) => {
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const currentPasswordRef = useRef(null);

  const submit = async (event) => {
    event.preventDefault();
    setFormError('');
    if (form.newPassword !== form.confirmPassword) {
      setFormError('New passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await api.changePassword(form.currentPassword, form.newPassword);
      setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      toast.success('Password changed. Older sessions have been signed out.');
    } catch (error) {
      const detail = error.response?.data?.detail;
      setFormError((typeof detail === 'string' ? detail : detail?.message) || 'Unable to change password');
      window.requestAnimationFrame(() => currentPasswordRef.current?.focus());
    } finally {
      setLoading(false);
    }
  };

  const fieldClass = "w-full px-4 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-100 focus:border-amber-500/50 outline-none";
  return (
    <div className="min-h-screen bg-zinc-950">
      <Navbar user={user} onLogout={onLogout} />
      <main className="max-w-3xl mx-auto p-6 lg:p-8">
        <nav aria-label="Security settings" className="mb-6 flex flex-wrap gap-2 text-sm">
          <span aria-current="page" className="rounded-lg bg-zinc-800 px-3 py-2 font-medium text-amber-300">Password</span>
          <Link to="/settings/ssh-identity" className="rounded-lg px-3 py-2 text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200">SSH devices</Link>
        </nav>
        <h1 className="text-3xl font-semibold text-zinc-100">Security</h1>
        <p className="mt-2 text-zinc-500">Manage the password for {user?.email}.</p>
        <section className="glass-card mt-8 rounded-xl border border-zinc-800/50 p-6 sm:p-8">
          <h2 className="text-lg font-semibold text-zinc-200">Change password</h2>
          <p className="mt-2 mb-6 text-sm text-zinc-500">You’ll remain signed in here; all older sessions will be revoked.</p>
          <form onSubmit={submit} className="space-y-5 max-w-lg">
            <div><label htmlFor="current-password" className="block text-sm text-zinc-400 mb-2">Current password</label><input ref={currentPasswordRef} id="current-password" type="password" autoComplete="current-password" className={fieldClass} value={form.currentPassword} onChange={(event) => setForm({ ...form, currentPassword: event.target.value })} required /></div>
            <div><label htmlFor="new-password" className="block text-sm text-zinc-400 mb-2">New password</label><input id="new-password" type="password" autoComplete="new-password" className={fieldClass} value={form.newPassword} onChange={(event) => setForm({ ...form, newPassword: event.target.value })} required /></div>
            <div><label htmlFor="confirm-password" className="block text-sm text-zinc-400 mb-2">Confirm new password</label><input id="confirm-password" type="password" autoComplete="new-password" className={fieldClass} value={form.confirmPassword} onChange={(event) => setForm({ ...form, confirmPassword: event.target.value })} required /></div>
            <PasswordRequirements />
            {formError && <p role="alert" className="rounded-lg border border-red-500/25 bg-red-500/10 p-3 text-sm text-red-200">{formError}</p>}
            <button disabled={loading} className="px-5 py-3 bg-amber-500 hover:bg-amber-400 text-zinc-950 rounded-lg font-semibold disabled:opacity-50">{loading ? 'Changing…' : 'Change password'}</button>
          </form>
        </section>
      </main>
    </div>
  );
};

export default SecuritySettings;
